import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import sh from "bundles/common/utils/sh";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import KPICard, { KPI_BAR_COLORS } from "./KPICard";
import { COLOR_PALETTE } from "./ChartBuilder";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      backgroundColor: "hsl(150 30% 12%)",
      borderRadius: "8px",
      px: 1.5,
      py: 1,
    }}>
      <Box sx={{ fontSize: 11, color: "hsl(150 10% 70%)", mb: 0.25, fontFamily: "'Segoe UI Regular', sans-serif" }}>
        {label || payload[0]?.name}
      </Box>
      {payload.map((entry, i) => (
        <Box key={i} sx={{ fontSize: 13, fontFamily: "'Segoe UI Semibold', sans-serif", color: "#fff" }}>
          {entry.value?.toLocaleString()}
        </Box>
      ))}
    </Box>
  );
};

const buildChartData = (data) => {
  if (!data?.labels || !data?.values) return [];
  return data.labels.map((label, i) => ({
    name: label,
    value: data.values[i] ?? 0,
  }));
};

const ChartDisplay = ({ chart }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!chart?.id) return;
    setLoading(true);
    setFailed(false);
    sh.get(`/internal/saved_charts/${chart.id}/data`)
      .then((res) => setData(res.data))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [chart?.id]);

  if (!chart) return null;
  if (loading) return <CircularProgress size={24} />;

  if (failed || !data) {
    const isKpi = chart.chart_type === "kpi";
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 0.5,
          p: isKpi ? "1.25rem" : 0,
          borderRadius: isKpi ? "0.75rem" : 0,
          border: isKpi ? "1px solid hsl(140 8% 88%)" : "none",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
        }}
      >
        {isKpi && (
          <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", letterSpacing: 0.8, textTransform: "uppercase" }}>
            {chart.title}
          </Typography>
        )}
        <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(0 72% 51%)" }}>
          Couldn&apos;t load chart data
        </Typography>
      </Box>
    );
  }

  if (chart.chart_type === "kpi") {
    return (
      <KPICard
        value={data.value}
        previousValue={data.previous_value}
        delta={data.delta}
        title={chart.title}
        barColor={KPI_BAR_COLORS[chart.id % KPI_BAR_COLORS.length]}
      />
    );
  }

  const chartData = buildChartData(data);
  const colors = chartData.map((_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length]);

  if (chart.chart_type === "bar") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}
            axisLine={{ stroke: "hsl(140 8% 88%)" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(140 8% 95%)" }} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={48}>
            {chartData.map((_, i) => (
              <Cell key={i} fill={colors[i]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (chart.chart_type === "line") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(140 8% 90%)" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}
            axisLine={{ stroke: "hsl(140 8% 88%)" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={COLOR_PALETTE[0]}
            strokeWidth={2.5}
            dot={{ fill: COLOR_PALETTE[0], strokeWidth: 2, r: 4, stroke: "#fff" }}
            activeDot={{ r: 6, stroke: COLOR_PALETTE[0], strokeWidth: 2, fill: "#fff" }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (chart.chart_type === "pie" || chart.chart_type === "doughnut") {
    const innerRadius = chart.chart_type === "doughnut" ? "55%" : 0;
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius="80%"
            paddingAngle={2}
            stroke="none"
          >
            {chartData.map((_, i) => (
              <Cell key={i} fill={colors[i]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData}>
        <Bar dataKey="value" fill={COLOR_PALETTE[0]} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ChartDisplay;
